import { useMemo } from "react";
import { GalleryImage, GalleryImageSource } from "./ImageGalleryTypes";

// Helper to detect image file extensions
const IMAGE_EXTENSIONS = /\.(jpg|jpeg|png|gif|webp|svg|bmp|ico)$/i;
const isImageFile = (name: string): boolean => IMAGE_EXTENSIONS.test(name);
const isBase64Image = (content: string): boolean =>
  content.startsWith("data:image/");

/**
 * Normalizes different data source formats into a uniform GalleryImage[].
 *
 * Supported source types:
 * - fileModels: FileModel[] from shared models (filter to images only)
 * - entityFiles: ServiceRequestFile / OfferFile arrays (with size)
 * - urls: Plain string URL array
 * - machine: Machine entity with optional image/catalogImageUrl/files fallback chain
 */
export function useGalleryImages(source: GalleryImageSource): GalleryImage[] {
  return useMemo(() => {
    switch (source.type) {
      case "fileModels": {
        if (!source.files || source.files.length === 0) return [];
        return source.files
          .filter((f) => {
            if (!f.content) return false;
            if (isBase64Image(f.content)) return true;
            if (f.name && isImageFile(f.name)) return true;
            return false;
          })
          .map((f, index) => ({
            id: `file-${f.name || index}-${index}`,
            src: f.content!,
            alt: f.name || `Image ${index + 1}`,
            name: f.name,
          }));
      }

      case "entityFiles": {
        if (!source.files || source.files.length === 0) return [];
        return source.files
          .filter((f) => {
            if (!f.content) return false;
            if (isBase64Image(f.content)) return true;
            if (f.name && isImageFile(f.name)) return true;
            return false;
          })
          .map((f, index) => ({
            id: `entity-${f.name}-${index}`,
            src: f.content,
            alt: f.name || `Image ${index + 1}`,
            name: f.name,
            size: f.size,
          }));
      }

      case "urls": {
        if (!source.urls || source.urls.length === 0) return [];
        return source.urls
          .filter((url) => !!url)
          .map((url, index) => ({
            id: `url-${index}`,
            src: url,
            alt: `Image ${index + 1}`,
          }));
      }

      case "machine": {
        const images: GalleryImage[] = [];

        // Priority 1: Files array (uploaded machine photos)
        if (source.files && source.files.length > 0) {
          source.files
            .filter((f) => {
              if (!f.content) return false;
              if (isBase64Image(f.content)) return true;
              if (f.name && isImageFile(f.name)) return true;
              return false;
            })
            .forEach((f, index) => {
              images.push({
                id: `machine-file-${f.name}-${index}`,
                src: f.content,
                alt: f.name || `Machine image ${index + 1}`,
                name: f.name,
              });
            });
        }

        // Priority 2: Single image field (legacy base64 or URL)
        if (images.length === 0 && source.image) {
          images.push({
            id: "machine-image",
            src: source.image,
            alt: "Machine image",
          });
        }

        // Priority 3: Catalog model images array (multiple angle/detail photos)
        if (images.length === 0 && source.catalogImages && source.catalogImages.length > 0) {
          source.catalogImages.forEach((img, index) => {
            images.push({
              id: `catalog-img-${img.id}`,
              src: img.imageStandardUrl,
              alt: `Catalog ${img.imageType || "image"} ${index + 1}`,
              isPrimary: img.isPrimary,
            });
          });
        }

        // Priority 4: Single catalog image URL (hero image from Techspex/catalog data)
        if (images.length === 0 && source.catalogImageUrl) {
          images.push({
            id: "machine-catalog",
            src: source.catalogImageUrl,
            alt: "Catalog image",
          });
        }

        return images;
      }

      default:
        return [];
    }
  }, [source]);
}
